import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Tooltip,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Email as EmailIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import AdminPage from "./AdminPage";
import { apiClient } from "../utils/apiClient";
import { TimestampDisplay } from "../internal/components/TimestampDisplay";
import { DatabaseObjectView } from "./DatabaseObjectView";
import { EmailThreadComponent } from "../components/EmailThreadComponent";
import {
  ActionLogErrorIndicator,
  ActionLogFilters,
  ActionLogExpandedContent,
  ActionLogTableRow,
  useActionLogFilters,
} from "./actionLog";
import type { ActionLogFiltersReturn } from "./actionLog";
import {
  ActionLogInfo,
  AdminListActionLogsBody,
  AdminListActionLogsResponse,
  DobbyActionType,
} from "@services/admin/types";

// Pagination constants
const ROWS_PER_PAGE_WHEN_GROUPED = 20;
const ROWS_PER_PAGE = 200;

interface OrganizationInfo {
  id: string;
  name: string;
}

interface UserInfo {
  id: string;
  email: string;
}

export function AdminActionLogPage() {
  const [actionLogs, setActionLogs] = useState<ActionLogInfo[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationInfo[]>([]);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState<"grouped" | "list">("grouped");
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_WHEN_GROUPED); // Default to grouped view

  // Filter states
  const filters = useActionLogFilters();

  // Dialog states
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(new Set());
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [databaseObjectViewOpen, setDatabaseObjectViewOpen] = useState(false);
  const [databaseObjectTable, setDatabaseObjectTable] = useState("");
  const [databaseObjectId, setDatabaseObjectId] = useState("");
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailId, setSelectedEmailId] = useState<string>("");

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Only load initial filters from URL params on first render
    if (!initialLoadComplete) {
      filters.setFiltersFromUrlParams(searchParams);
      
      const initialViewMode = searchParams.get("viewMode");
      if (initialViewMode === "list" || initialViewMode === "grouped") {
        setViewMode(initialViewMode);
      }

      setFiltersInitialized(true);
      setInitialLoadComplete(true);
      fetchOrganizations();
      fetchUsers();
    }
  }, [searchParams, filters, initialLoadComplete]);

  useEffect(() => {
    // Only fetch action logs on initial load and pagination/view mode changes
    // Note: includeDescription changes should trigger fetchActionLogs via handleSearch()
    // Note: viewMode changes are handled via rowsPerPage changes, so no need to depend on viewMode directly
    if (filtersInitialized) {
      fetchActionLogs();
    }
  }, [filtersInitialized, page, rowsPerPage]);

  const fetchOrganizations = async () => {
    try {
      const res = await apiClient.post<{ organizations: OrganizationInfo[] }>(
        `/admin/organizations/list`,
        {}
      );
      setOrganizations(res.organizations);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiClient.post<{ users: UserInfo[] }>(
        `/admin/list_users`,
        {}
      );
      setUsers(res.users);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchActionLogs = async (overrideViewMode?: "grouped" | "list") => {
    try {
      setLoading(true);
      setError(null);

      const currentViewMode = overrideViewMode || viewMode;
      const currentRowsPerPage = currentViewMode === "grouped" ? ROWS_PER_PAGE_WHEN_GROUPED : ROWS_PER_PAGE;
      const requestBody: AdminListActionLogsBody = {
        limit: currentRowsPerPage,
        offset: page * currentRowsPerPage,
        viewMode: currentViewMode,
        errorsOnly: filters.errorsOnly,
        includeDescription: filters.includeDescription,
      };

      if (filters.errorRegex) requestBody.errorRegex = filters.errorRegex;

      if (filters.selectedOrgId) requestBody.orgId = filters.selectedOrgId;
      if (filters.selectedUserId) requestBody.userId = filters.selectedUserId;
      if (filters.selectedTypes.length > 0) requestBody.types = filters.selectedTypes;
      if (filters.emailFilter) requestBody.emailId = filters.emailFilter;
      if (filters.extractedDataFilter) requestBody.extractedDataId = filters.extractedDataFilter;
      if (filters.derivedDataFilter) requestBody.derivedDataId = filters.derivedDataFilter;
      if (filters.messageIdFilter) requestBody.messageId = filters.messageIdFilter;
      if (filters.recurringWorkflowFilter)
        requestBody.recurringWorkflowId = filters.recurringWorkflowFilter;
      if (filters.workflowFilter) requestBody.workflowId = filters.workflowFilter;
      if (filters.reminderFilter) requestBody.reminderId = filters.reminderFilter;
      if (filters.shipmentFeedFilter) requestBody.shipmentFeedId = filters.shipmentFeedFilter;
      if (filters.startDate) requestBody.startDate = new Date(filters.startDate).toISOString();
      if (filters.endDate) requestBody.endDate = new Date(filters.endDate).toISOString();

      const res = await apiClient.post<AdminListActionLogsResponse>(
        `/admin/action-logs/list`,
        requestBody
      );

      setActionLogs(res.logs);
      setTotal(res.total);
    } catch (error) {
      console.error("Error fetching action logs:", error);
      setError("Failed to load action logs");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const viewLogDetails = (log: ActionLogInfo) => {
    setDatabaseObjectTable("dobbyActionLog");
    setDatabaseObjectId(log.id);
    setDatabaseObjectViewOpen(true);
  };

  const handleGptRequestClick = (gptRequestId: string) => {
    setDatabaseObjectTable("gptRequest");
    setDatabaseObjectId(gptRequestId);
    setDatabaseObjectViewOpen(true);
  };

  const handleEmailClick = (emailId: string) => {
    setSelectedEmailId(emailId);
    setEmailDialogOpen(true);
  };

  const handleExtractedDataClick = (extractedDataId: string) => {
    window.open(`/www/admin/extracted-data?id=${extractedDataId}`, "_blank");
  };
  const handleDerivedDataClick = (derivedDataId: string) => {
    window.open(
      `/www/admin/extracted-data?tab=derived-data&id=${derivedDataId}`,
      "_blank"
    );
  };

  const handleAIAgentTraceClick = (log: ActionLogInfo) => {
    // Check if extra data contains trace_id or openai_trace_url
    const traceUrl = `https://platform.openai.com/traces/trace?trace_id=${log.extra.traceId}`;
    window.open(traceUrl, "_blank");
  };

  const handleErrorsClick = (log: ActionLogInfo) => {
    setDatabaseObjectTable("dobbyActionLog");
    setDatabaseObjectId(log.id);
    setDatabaseObjectViewOpen(true);
  };

  const handleReminderClick = (reminderId: string) => {
    setDatabaseObjectTable("shipmentReminder");
    setDatabaseObjectId(reminderId);
    setDatabaseObjectViewOpen(true);
  };

  const handleSearch = () => {
    setPage(0); // Reset to first page

    // Update URL parameters to reflect current filter state
    const newSearchParams = filters.getUrlSearchParams();
    newSearchParams.set("viewMode", viewMode);

    // Update the URL without triggering a page reload
    navigate(`/admin/action-logs?${newSearchParams.toString()}`, {
      replace: true,
    });

    fetchActionLogs();
  };

  const clearFilters = () => {
    filters.clearFilters();
    setPage(0);

    // Clear URL parameters as well
    navigate("/admin/action-logs", { replace: true });

    // Trigger search with cleared filters
    setTimeout(() => fetchActionLogs(), 0);
  };

  const toggleEmailExpand = (emailId: string) => {
    const newExpanded = new Set(expandedEmails);
    if (newExpanded.has(emailId)) {
      newExpanded.delete(emailId);
    } else {
      newExpanded.add(emailId);
    }
    setExpandedEmails(newExpanded);
  };

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newViewMode: "grouped" | "list" | null
  ) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
      setExpandedEmails(new Set()); // Clear expanded state when switching modes
      setPage(0); // Reset to first page when changing view mode
      
      // Update rowsPerPage immediately
      setRowsPerPage(
        newViewMode === "grouped" ? ROWS_PER_PAGE_WHEN_GROUPED : ROWS_PER_PAGE
      );
      
      // Update URL to reflect the new view mode
      const newSearchParams = filters.getUrlSearchParams();
      newSearchParams.set("viewMode", newViewMode);
      navigate(`/admin/action-logs?${newSearchParams.toString()}`, {
        replace: true,
      });
      
      // Fetch data with new parameters (pass the new view mode to avoid stale state)
      setTimeout(() => fetchActionLogs(newViewMode), 0);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "EmailSend":
        return "primary";
      case "GptRequest":
        return "secondary";
      case "CreateShipment":
      case "UpdateShipment":
        return "success";
      case "StartAIAgent":
        return "warning";
      case "EndAIAgent":
        return "success";
      default:
        return "default";
    }
  };

  // Group logs by email for display
  const groupedLogs = actionLogs.reduce(
    (acc, log) => {
      const emailId = log.emailId || "no-email";
      if (!acc[emailId]) {
        acc[emailId] = [];
      }
      acc[emailId].push(log);
      return acc;
    },
    {} as Record<string, ActionLogInfo[]>
  );

  // Sort each group's logs by createdAt (most recent first) and sort email groups by most recent log
  const sortedGroupedLogs = Object.entries(groupedLogs)
    .map(([emailId, logs]) => {
      // Sort logs within each email group by createdAt desc
      const sortedLogs = logs.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      return [emailId, sortedLogs] as [string, ActionLogInfo[]];
    })
    // Sort email groups by the most recent log in each group
    .sort(
      ([, logsA], [, logsB]) =>
        new Date(logsB[0].createdAt).getTime() -
        new Date(logsA[0].createdAt).getTime()
    );

  if (loading && actionLogs.length === 0) {
    return (
      <AdminPage>
        <Box
          sx={{ display: "flex", justifyContent: "center", padding: "40px" }}
        >
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <Box sx={{ padding: "20px" }}>
        <Box
          sx={{ display: "flex", alignItems: "center", marginBottom: "16px" }}
        >
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            Action Log Management
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
            >
              <ToggleButton value="grouped">
                <ViewModuleIcon sx={{ mr: 1 }} />
                Grouped by Email
              </ToggleButton>
              <ToggleButton value="list">
                <ViewListIcon sx={{ mr: 1 }} />
                Full List
              </ToggleButton>
            </ToggleButtonGroup>
            <Button variant="contained" onClick={handleSearch} sx={{ mr: 1 }}>
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={clearFilters}
              disabled={!filters.hasActiveFilters}
            >
              Clear Filters
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{ marginBottom: "16px" }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Filters */}
        <ActionLogFilters
          filters={filters}
          viewMode={viewMode}
          organizations={organizations}
          users={users}
          total={total}
          actionLogsCount={actionLogs.length}
          groupedLogsCount={Object.keys(groupedLogs).length}
          handleSearch={handleSearch}
        />

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {viewMode === "grouped" ? (
                  <>
                    <TableCell>
                      <strong>Email</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Type</strong>
                    </TableCell>
                    <TableCell>
                      <strong>User</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Created</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Errors</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Actions</strong>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>
                      <strong>Type</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Email</strong>
                    </TableCell>
                    <TableCell>
                      <strong>User</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Created</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Memo</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Errors</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Actions</strong>
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {actionLogs.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={viewMode === "grouped" ? 6 : 7}
                    align="center"
                  >
                    No action logs found
                  </TableCell>
                </TableRow>
              ) : viewMode === "grouped" ? (
                sortedGroupedLogs.map(([emailId, logs]) => {
                  const firstLog = logs[0];
                  const isExpanded = expandedEmails.has(emailId);
                  const emailSubject = firstLog.email?.subject || "No Email";

                  return (
                    <React.Fragment key={emailId}>
                      {/* Email group header */}
                      <TableRow sx={{ bgcolor: "grey.50" }}>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => toggleEmailExpand(emailId)}
                            >
                              {isExpanded ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )}
                            </IconButton>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                sx={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={emailSubject}
                              >
                                {emailSubject}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={`${firstLog.email?.fromEmail || "Unknown sender"} (${logs.length} actions)`}
                              >
                                {firstLog.email?.fromEmail || "Unknown sender"}{" "}
                                ({logs.length} actions)
                              </Typography>
                            </Box>
                            {emailId !== "no-email" && (
                              <Tooltip title="View Email Object">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEmailClick(emailId)}
                                >
                                  <EmailIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            flexWrap="wrap"
                            sx={{ gap: "2px" }}
                          >
                            {Array.from(
                              new Set(logs.map((log) => log.type))
                            ).map((type) => (
                              <Chip
                                key={type}
                                label={type}
                                size="small"
                                color={getTypeColor(type) as any}
                                sx={{ mb: "2px" }}
                              />
                            ))}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {firstLog.user?.email || "Unknown user"}
                        </TableCell>
                        <TableCell>
                          <TimestampDisplay timestamp={logs[0].createdAt} />
                        </TableCell>
                        <TableCell align="center">
                          <ActionLogErrorIndicator
                            log={logs[0]}
                            logs={logs}
                            onClick={() => toggleEmailExpand(emailId)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => toggleEmailExpand(emailId)}
                            title={isExpanded ? "Collapse" : "Expand"}
                          >
                            {isExpanded ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>

                      {/* Expanded action logs */}
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          sx={{ p: 0, border: 0 }}
                        >
                          <ActionLogExpandedContent
                            logs={logs}
                            isExpanded={isExpanded}
                            includeDescription={filters.includeDescription}
                            displayErrors={filters.displayErrors}
                            onViewDetails={viewLogDetails}
                            onViewGptRequest={handleGptRequestClick}
                            onViewExtractedData={handleExtractedDataClick}
                            onViewDerivedData={handleDerivedDataClick}
                            onViewAIAgentTrace={handleAIAgentTraceClick}
                            onViewErrors={handleErrorsClick}
                          />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              ) : (
                // List view - flat table (ensure sorted by most recent first)
                actionLogs
                  .sort(
                    (a, b) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime()
                  )
                  .map((log) => (
                    <ActionLogTableRow
                      key={log.id}
                      log={log}
                      includeDescription={filters.includeDescription}
                      displayErrors={filters.displayErrors}
                      onViewDetails={viewLogDetails}
                      onViewGptRequest={handleGptRequestClick}
                      onViewExtractedData={handleExtractedDataClick}
                      onViewDerivedData={handleDerivedDataClick}
                      onViewShipmentEmail={handleExtractedDataClick}
                      onViewShipmentFeed={handleDerivedDataClick}
                      onViewAIAgentTrace={handleAIAgentTraceClick}
                      onViewErrors={handleErrorsClick}
                      onViewEmail={handleEmailClick}
                      onViewReminder={handleReminderClick}
                    />
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={
            viewMode === "grouped"
              ? [ROWS_PER_PAGE_WHEN_GROUPED]
              : [ROWS_PER_PAGE]
          }
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        {/* Database Object View Dialog */}
        <DatabaseObjectView
          open={databaseObjectViewOpen}
          onClose={() => setDatabaseObjectViewOpen(false)}
          initialTable={databaseObjectTable}
          initialId={databaseObjectId}
          autoFetch={true}
        />

        {/* Email Thread Dialog */}
        {emailDialogOpen && selectedEmailId && (
          <Dialog
            open={emailDialogOpen}
            onClose={() => setEmailDialogOpen(false)}
            maxWidth="lg"
            fullWidth
            sx={{
              "& .MuiDialog-paper": {
                height: "90vh",
              },
            }}
          >
            <IconButton
              aria-label="close"
              onClick={() => setEmailDialogOpen(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
                zIndex: 1,
              }}
            >
              <CloseIcon />
            </IconButton>
            <EmailThreadComponent emailId={selectedEmailId} />
          </Dialog>
        )}
      </Box>
    </AdminPage>
  );
}
